import { FC } from 'react';
import { Form, message, Modal, Select, Button, Flex } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { KycDoc, useUpdateKycDocument } from './useKycDocuments';

export const EditDocumentModal: FC<{
  customerId: string;
  doc: KycDoc;
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose, customerId, doc }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();

  const mutation = useUpdateKycDocument();

  const [form] = Form.useForm();

  const onFinish = (values: Partial<KycDoc>) => {
    mutation
      .mutateAsync({
        customerId,
        documentId: doc.id,
        payload: values,
      })
      .then(() => {
        messageApi.success('Successfully edited!');
        onClose();
        return queryClient.invalidateQueries({
          queryKey: ['kyc-docs'],
        });
      })
      .catch((err) => {
        messageApi.error(
          err.response.status === 424 ? `Error: ${err?.response?.data?.error}` : 'Something is wrong',
          6000
        );
      });
  };

  return (
    <>
      {contextHolder}
      <Modal
        loading={mutation.isPending}
        title="Edit Document"
        destroyOnClose
        footer={null}
        open={open}
        onCancel={onClose}
      >
        <Form
          onFinish={onFinish}
          name="editKYCVerification"
          form={form}
          initialValues={{
            doc_type: doc.doc_type,
            doc_subtype: doc.doc_subtype,
            doc_side: doc.doc_side,
          }}
          requiredMark={false}
          style={{
            paddingTop: 20,
          }}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            rules={[
              {
                required: true,
                message: 'Please select',
              },
            ]}
            name="doc_type"
            layout="vertical"
            label="Document Type"
          >
            <Select placeholder="Select value...">
              <Select.Option value="id_document">ID Document</Select.Option>
              <Select.Option value="proof_of_address">Proof of Address</Select.Option>
              <Select.Option value="proof_of_source_of_funds">Proof of Source of Funds</Select.Option>
              <Select.Option value="business_formation">Business Formation</Select.Option>
              <Select.Option value="directors_registry">Directors Registry</Select.Option>
              <Select.Option value="shareholder_registry">Shareholder Registry</Select.Option>
              <Select.Option value="proof_of_nature_of_business">Proof of Nature of Business</Select.Option>
              <Select.Option value="financial_statements">Financial Statements</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            rules={[
              {
                required: true,
                message: 'Please select',
              },
            ]}
            name="doc_subtype"
            layout="vertical"
            label="Document Subtype"
          >
            <Select placeholder="Select value...">
              <Select.Option value="government_id">Government ID</Select.Option>
              <Select.Option value="passport">Passport</Select.Option>
              <Select.Option value="residence_permit">Residence Permit</Select.Option>
              <Select.Option value="driver_license">Driver License</Select.Option>
              <Select.Option value="utility_bill">Utility Bill</Select.Option>
              <Select.Option value="state_or_provincial_id">State or Provincial ID</Select.Option>
              <Select.Option value="bank_statement">Bank Statement</Select.Option>
              <Select.Option value="official_government_correspondence">
                Official Government Correspondence
              </Select.Option>
              <Select.Option value="rental_or_mortgage_statement">Rental or Mortgage Statement</Select.Option>
              <Select.Option value="tax_document">Tax Document</Select.Option>
              <Select.Option value="invoice">Invoice</Select.Option>
              <Select.Option value="voter_registration_card">Voter Registration Card</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="doc_side" layout="vertical" label="Document Side">
            <Select placeholder="Select value..." allowClear>
              <Select.Option value="front">Front</Select.Option>
              <Select.Option value="back">Back</Select.Option>
            </Select>
          </Form.Item>

          <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Edit
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
